import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocale } from '../../../hooks/useLocale';
import { SUPPORTED_LANGUAGES, getLanguageByCode } from './languageConfig';

/**
 * LanguageSwitcher - A custom dropdown component for language selection
 *
 * @param {Object} props
 * @param {'compact' | 'full' | 'minimal'} [props.variant='compact'] - Display variant
 * @param {'left' | 'right'} [props.dropdownAlign='right'] - Dropdown alignment
 * @param {boolean} [props.showLabel=true] - Show language name/code
 * @param {boolean} [props.showFlag=true] - Show flag emoji
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {Function} [props.onChange] - Callback after language change
 */
export default function LanguageSwitcher({
  variant = 'compact',
  dropdownAlign = 'right',
  showLabel = true,
  showFlag = true,
  className = '',
  onChange,
}) {
  const { currentLanguage, changeLanguage, supportedLanguages } = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Filter SUPPORTED_LANGUAGES to only include languages that are actually supported by i18n
  const availableLanguages = SUPPORTED_LANGUAGES.filter(lang =>
    supportedLanguages.includes(lang.code)
  );

  const currentLang = getLanguageByCode(currentLanguage);
  const currentIndex = availableLanguages.findIndex(lang => lang.code === currentLanguage);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Reset highlighted index when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(currentIndex >= 0 ? currentIndex : 0);
    }
  }, [isOpen, currentIndex]);

  // Handle language selection
  const handleSelect = useCallback(async (code) => {
    setIsOpen(false);
    if (code !== currentLanguage) {
      await changeLanguage(code);
      onChange?.(code);
    }
    triggerRef.current?.focus();
  }, [currentLanguage, changeLanguage, onChange]);

  // Keyboard navigation
  const handleKeyDown = useCallback((event) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (isOpen) {
          handleSelect(availableLanguages[highlightedIndex].code);
        } else {
          setIsOpen(true);
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (isOpen) {
          setHighlightedIndex(prev =>
            prev < availableLanguages.length - 1 ? prev + 1 : 0
          );
        } else {
          setIsOpen(true);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (isOpen) {
          setHighlightedIndex(prev =>
            prev > 0 ? prev - 1 : availableLanguages.length - 1
          );
        } else {
          setIsOpen(true);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setIsOpen(false);
        triggerRef.current?.focus();
        break;
      case 'Tab':
        setIsOpen(false);
        break;
      default:
        break;
    }
  }, [isOpen, highlightedIndex, availableLanguages, handleSelect]);

  // Get trigger label based on variant
  const getTriggerLabel = () => {
    switch (variant) {
      case 'minimal':
        return null;
      case 'full':
        return `${currentLang.nativeName} (${currentLang.englishName})`;
      case 'compact':
      default:
        return currentLang.code.toUpperCase();
    }
  };

  // Get dropdown item label based on variant
  const getItemLabel = (lang) => {
    switch (variant) {
      case 'minimal':
        return lang.code.toUpperCase();
      case 'full':
        return `${lang.nativeName} (${lang.englishName})`;
      case 'compact':
      default:
        return lang.nativeName;
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        className={`
          inline-flex items-center gap-2
          border border-gray-300 rounded-md bg-white
          text-sm font-medium text-gray-700
          hover:border-gray-400
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
          transition-colors
          ${variant === 'minimal' ? 'px-2 py-1.5' : 'px-3 py-2'}
          ${isOpen ? 'border-primary-500' : ''}
        `}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls="language-menu"
        aria-label={`Select language, current: ${currentLang.nativeName}`}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
      >
        {showFlag && (
          <span className="text-base" role="img" aria-hidden="true">
            {currentLang.flag}
          </span>
        )}
        {showLabel && getTriggerLabel() && (
          <span>{getTriggerLabel()}</span>
        )}
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          id="language-menu"
          role="listbox"
          aria-label="Available languages"
          aria-activedescendant={`lang-option-${availableLanguages[highlightedIndex]?.code}`}
          tabIndex={-1}
          className={`
            absolute z-50 mt-2 py-1
            min-w-[180px] max-h-60 overflow-y-auto
            bg-white rounded-md shadow-lg
            border border-gray-200
            ${dropdownAlign === 'right' ? 'right-0' : 'left-0'}
            animate-fade-in
          `}
          style={{
            animation: 'fadeIn 0.15s ease-out',
          }}
        >
          {availableLanguages.map((lang, index) => (
            <div
              key={lang.code}
              id={`lang-option-${lang.code}`}
              role="option"
              aria-selected={lang.code === currentLanguage}
              className={`
                flex items-center gap-3 px-4 py-2.5 cursor-pointer
                text-sm transition-colors
                ${index === highlightedIndex ? 'bg-gray-100' : ''}
                ${lang.code === currentLanguage ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'}
              `}
              onClick={() => handleSelect(lang.code)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {showFlag && (
                <span className="text-lg" role="img" aria-hidden="true">
                  {lang.flag}
                </span>
              )}
              <span className="flex-1">{getItemLabel(lang)}</span>
              {lang.code === currentLanguage && (
                <CheckIcon className="h-4 w-4 text-primary-600" aria-hidden="true" />
              )}
            </div>
          ))}
        </div>
      )}

      {/* CSS for animation */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-4px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.15s ease-out;
        }
      `}</style>
    </div>
  );
}

// Inline SVG icons to avoid external dependencies
function ChevronDownIcon({ className }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function CheckIcon({ className }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}
